import React, { useEffect, useState, lazy, Suspense } from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, useNavigate, Navigate } from 'react-router-dom';

// Core components (loaded immediately)
import AdminLayout from './components/AdminLayout';
import { AuthProvider, useAuth } from './components/Auth/AuthContext';
import PrivateRoute from './components/Auth/PrivateRoute';
import ProtectedHostUpgradeRoute from './components/Auth/ProtectedHostUpgradeRoute';
import ErrorBoundary from './components/ErrorBoundary';
import Footer from './components/Footer';
import HostLayout from './components/HostLayout';
import Navbar from './components/Navbar';
import ToastProvider from './components/ToastNotification';
import { HostUpgradeProvider } from './contexts/HostUpgradeContext';
import { MessagingProvider } from './contexts/MessagingContext';

// Lazy load all route components
const Login = lazy(() => import('./components/Auth/Login'));
const Register = lazy(() => import('./components/Auth/Register'));
const EventDetail = lazy(() => import('./components/EventDetail'));
const EventGenerator = lazy(() => import('./components/EventGenerator'));
const EventGeneratorSimple = lazy(() => import('./components/EventGeneratorSimple'));
const HostDashboardWrapper = lazy(() => import('./components/HostDashboardWrapper'));
const UserOnboarding = lazy(() => import('./components/Onboarding/UserOnboarding'));

// Lazy load all pages
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const AdminEventCreator = lazy(() => import('./pages/AdminEventCreator'));
const AdminEventEditor = lazy(() => import('./pages/AdminEventEditor'));
const AdminEvents = lazy(() => import('./pages/AdminEvents'));
const AdminHostTools = lazy(() => import('./pages/AdminHostTools'));
const AdminUsers = lazy(() => import('./pages/AdminUsers'));
const AnalyticalDashboard = lazy(() => import('./pages/AnalyticalDashboard'));
const AnalyticsDashboard = lazy(() => import('./pages/AnalyticsDashboard'));
const ApiDocs = lazy(() => import('./pages/ApiDocs'));
const BecomeAHost = lazy(() => import('./pages/BecomeAHost'));
const BecomeHost = lazy(() => import('./components/BecomeHost'));
const Checkout = lazy(() => import('./pages/Checkout'));
const DeveloperPortal = lazy(() => import('./pages/DeveloperPortal'));
const Discover = lazy(() => import('./pages/Discover'));
const EventCreator = lazy(() => import('./pages/EventCreator'));
const EventsListPage = lazy(() => import('./pages/EventsListPageEnhanced'));
const Home = lazy(() => import('./pages/Home'));
const HostManagement = lazy(() => import('./pages/HostManagement'));
const HostPayouts = lazy(() => import('./pages/HostPayouts'));
const MyEvents = lazy(() => import('./pages/MyEvents'));
const MySavedEvents = lazy(() => import('./pages/MySavedEvents'));
const NotificationCenter = lazy(() => import('./pages/NotificationCenter'));
const NotificationPreferences = lazy(() => import('./pages/NotificationPreferences'));
const Onboarding = lazy(() => import('./pages/Onboarding'));
const Orders = lazy(() => import('./pages/Orders'));
const UserProfile = lazy(() => import('./pages/UserProfile'));
const Messages = lazy(() => import('./pages/Messages'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const TermsAndConditions = lazy(() => import('./pages/TermsAndConditions'));

// Host upgrade flow components
const HostPaymentPlan = lazy(() => import('./components/HostPaymentPlan'));
const StripePayment = lazy(() => import('./components/StripePayment'));
const EnterpriseContact = lazy(() => import('./components/EnterpriseContact'));
const EnterpriseSuccess = lazy(() => import('./components/EnterpriseSuccess'));
const PaymentSuccess = lazy(() => import('./components/PaymentSuccess'));
const PaymentError = lazy(() => import('./components/PaymentError'));
const FreeHostSignup = lazy(() => import('./components/FreeHostSignup'));

// Host-specific pages
const HostDashboard = lazy(() => import('./pages/HostDashboard'));
const HostMessages = lazy(() => import('./pages/HostMessages'));
const HostEvents = lazy(() => import('./pages/HostEvents'));
const HostEventCreator = lazy(() => import('./pages/HostEventCreator'));
const HostTools = lazy(() => import('./pages/HostTools'));
const HostSocialTest = lazy(() => import('./pages/HostSocialTest'));
const HostAnalytics = lazy(() => import('./pages/HostAnalytics'));
const HostAttendees = lazy(() => import('./pages/HostAttendees'));
const HostSettings = lazy(() => import('./pages/HostSettings'));

// Design system comparison
const DesignSystemComparison = lazy(() => import('./pages/DesignSystemComparison'));

// Loading component
const LoadingFallback = () => (
  <div
    style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh',
      fontSize: '18px',
      color: '#666',
    }}
  >
    Loading...
  </div>
);

// Onboarding wrapper component
function OnboardingWrapper({ children }) {
  const { user, needsOnboarding, setNeedsOnboarding, checkOnboardingStatus } = useAuth();
  const navigate = useNavigate();
  const [onboardingChecked, setOnboardingChecked] = useState(false);

  useEffect(() => {
    // Check if onboarding has been manually disabled
    const skipOnboarding = localStorage.getItem('skipOnboarding') === 'true';

    // Check onboarding status when user logs in or app loads
    if (user && !onboardingChecked && !skipOnboarding) {
      checkOnboardingStatus(user.id).then(() => {
        setOnboardingChecked(true);
      });
    } else if (skipOnboarding) {
      setNeedsOnboarding(false);
      setOnboardingChecked(true);
    }
  }, [user, onboardingChecked, checkOnboardingStatus, setNeedsOnboarding]);

  if (needsOnboarding && user) {
    return (
      <Suspense fallback={<LoadingFallback />}>
        <UserOnboarding
          onComplete={() => {
            setNeedsOnboarding(false);
            navigate('/discover');
          }}
          onSkip={() => {
            setNeedsOnboarding(false);
            navigate('/discover');
          }}
        />
      </Suspense>
    );
  }

  return children;
}

function AppContent() {
  return (
    <OnboardingWrapper>
      <div className="App">
        <Suspense fallback={<LoadingFallback />}>
          <Routes>
            {/* Legacy host-tools redirects to new /host paths */}
            <Route path="/host-dashboard" element={<Navigate to="/host" replace />} />
            <Route path="/host-tools" element={<Navigate to="/host" replace />} />
            <Route path="/host-tools/tools" element={<Navigate to="/host/tools" replace />} />
            <Route
              path="/host-tools/generate"
              element={<Navigate to="/host/events/create" replace />}
            />
            <Route
              path="/host-tools/analytics"
              element={<Navigate to="/host/analytics" replace />}
            />
            <Route path="/host-tools/*" element={<Navigate to="/host" replace />} />
            {/* Admin Routes - Protected for admin users only */}
            <Route
              path="/admin/*"
              element={
                <PrivateRoute requiredUserType="admin">
                  <AdminLayout />
                </PrivateRoute>
              }
            >
              <Route index element={<AdminDashboard />} />
              <Route path="users" element={<AdminUsers />} />
              <Route path="events" element={<AdminEvents />} />
              <Route path="events/create" element={<AdminEventCreator />} />
              <Route path="events/edit/:id" element={<AdminEventEditor />} />
              <Route path="host-tools" element={<AdminHostTools />} />
            </Route>

            {/* Host Routes - Protected for approved hosts */}
            <Route
              path="/host/*"
              element={
                <PrivateRoute requiredUserType="host">
                  <HostLayout />
                </PrivateRoute>
              }
            >
              <Route index element={<HostDashboard />} />
              <Route path="messages" element={<HostMessages />} />
              <Route path="events" element={<HostEvents />} />
              <Route path="events/create" element={<HostEventCreator />} />
              <Route path="tools" element={<HostTools />} />
              <Route path="tools/social-test" element={<HostSocialTest />} />
              <Route path="analytics" element={<HostAnalytics />} />
              <Route path="attendees" element={<HostAttendees />} />
              <Route path="payouts" element={<HostPayouts />} />
              <Route path="settings" element={<HostSettings />} />
            </Route>

            {/* Regular Routes - With Navbar/Footer */}
            <Route
              path="/"
              element={
                <>
                  <Navbar />
                  <Home />
                  <Footer />
                </>
              }
            />
            <Route
              path="/home"
              element={
                <>
                  <Navbar />
                  <Home />
                  <Footer />
                </>
              }
            />
            <Route
              path="/login"
              element={
                <>
                  <Navbar />
                  <Login />
                  <Footer />
                </>
              }
            />
            <Route
              path="/register"
              element={
                <>
                  <Navbar />
                  <Register />
                  <Footer />
                </>
              }
            />
            <Route
              path="/profile"
              element={
                <>
                  <Navbar />
                  <UserProfile />
                  <Footer />
                </>
              }
            />
            <Route
              path="/discover"
              element={
                <>
                  <Navbar />
                  <Discover />
                  <Footer />
                </>
              }
            />
            <Route
              path="/event/:id"
              element={
                <>
                  <Navbar />
                  <EventDetail />
                  <Footer />
                </>
              }
            />
            <Route
              path="/events/analytics"
              element={
                <>
                  <Navbar />
                  <EventsListPage />
                  <Footer />
                </>
              }
            />
            <Route
              path="/events/analytics/:eventId"
              element={
                <>
                  <Navbar />
                  <AnalyticalDashboard />
                  <Footer />
                </>
              }
            />
            <Route
              path="/generate"
              element={
                <>
                  <Navbar />
                  <EventGenerator />
                  <Footer />
                </>
              }
            />
            <Route
              path="/generate-simple"
              element={
                <>
                  <Navbar />
                  <EventGeneratorSimple />
                  <Footer />
                </>
              }
            />
            <Route
              path="/my-events"
              element={
                <>
                  <Navbar />
                  <MyEvents />
                  <Footer />
                </>
              }
            />
            <Route
              path="/saved-events"
              element={
                <>
                  <Navbar />
                  <MySavedEvents />
                  <Footer />
                </>
              }
            />
            <Route
              path="/checkout"
              element={
                <>
                  <Navbar />
                  <Checkout />
                  <Footer />
                </>
              }
            />
            <Route
              path="/orders"
              element={
                <>
                  <Navbar />
                  <Orders />
                  <Footer />
                </>
              }
            />
            <Route
              path="/design-system-comparison"
              element={
                <>
                  <Navbar />
                  <DesignSystemComparison />
                  <Footer />
                </>
              }
            />
            <Route
              path="/event-creator"
              element={
                <>
                  <Navbar />
                  <EventCreator />
                  <Footer />
                </>
              }
            />
            <Route
              path="/notifications"
              element={
                <>
                  <Navbar />
                  <NotificationCenter />
                  <Footer />
                </>
              }
            />
            <Route
              path="/messages"
              element={
                <>
                  <Navbar />
                  <Messages />
                  <Footer />
                </>
              }
            />
            <Route
              path="/notification-preferences"
              element={
                <>
                  <Navbar />
                  <NotificationPreferences />
                  <Footer />
                </>
              }
            />
            <Route
              path="/analytics-dashboard"
              element={
                <>
                  <Navbar />
                  <AnalyticsDashboard />
                  <Footer />
                </>
              }
            />
            <Route
              path="/onboarding"
              element={
                <>
                  <Navbar />
                  <Onboarding />
                  <Footer />
                </>
              }
            />
            <Route
              path="/api-docs"
              element={
                <>
                  <Navbar />
                  <ApiDocs />
                  <Footer />
                </>
              }
            />
            <Route
              path="/become-a-host"
              element={
                <>
                  <Navbar />
                  <BecomeAHost />
                  <Footer />
                </>
              }
            />
            {/* Host Upgrade Flow Routes */}
            <Route
              path="/become-host"
              element={
                <>
                  <Navbar />
                  <BecomeHost />
                  <Footer />
                </>
              }
            />
            <Route
              path="/become-host/signup"
              element={
                <>
                  <Navbar />
                  <FreeHostSignup />
                  <Footer />
                </>
              }
            />
            <Route
              path="/become-host/plans"
              element={
                <ProtectedHostUpgradeRoute>
                  <Navbar />
                  <HostPaymentPlan />
                  <Footer />
                </ProtectedHostUpgradeRoute>
              }
            />
            <Route
              path="/become-host/payment"
              element={
                <ProtectedHostUpgradeRoute>
                  <Navbar />
                  <StripePayment />
                  <Footer />
                </ProtectedHostUpgradeRoute>
              }
            />
            <Route
              path="/become-host/success"
              element={
                <>
                  <Navbar />
                  <PaymentSuccess />
                  <Footer />
                </>
              }
            />
            <Route
              path="/become-host/error"
              element={
                <>
                  <Navbar />
                  <PaymentError />
                  <Footer />
                </>
              }
            />
            <Route
              path="/become-host/enterprise"
              element={
                <ProtectedHostUpgradeRoute>
                  <Navbar />
                  <EnterpriseContact />
                  <Footer />
                </ProtectedHostUpgradeRoute>
              }
            />
            <Route
              path="/become-host/enterprise/success"
              element={
                <>
                  <Navbar />
                  <EnterpriseSuccess />
                  <Footer />
                </>
              }
            />
            <Route
              path="/developer-portal"
              element={
                <>
                  <Navbar />
                  <DeveloperPortal />
                  <Footer />
                </>
              }
            />
            <Route
              path="/host-management"
              element={
                <>
                  <Navbar />
                  <HostManagement />
                  <Footer />
                </>
              }
            />
            <Route
              path="/host-dashboard"
              element={
                <>
                  <Navbar />
                  <HostDashboardWrapper />
                  <Footer />
                </>
              }
            />
            <Route
              path="/host-payouts"
              element={
                <>
                  <Navbar />
                  <HostPayouts />
                  <Footer />
                </>
              }
            />
            <Route
              path="/privacy"
              element={
                <>
                  <Navbar />
                  <PrivacyPolicy />
                  <Footer />
                </>
              }
            />
            <Route
              path="/terms"
              element={
                <>
                  <Navbar />
                  <TermsAndConditions />
                  <Footer />
                </>
              }
            />
          </Routes>
        </Suspense>
      </div>
    </OnboardingWrapper>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true,
        }}
      >
        <ToastProvider>
          <AuthProvider>
            <MessagingProvider>
              <HostUpgradeProvider>
                <AppContent />
              </HostUpgradeProvider>
            </MessagingProvider>
          </AuthProvider>
        </ToastProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
